import { DEFAULT_CURRENCY } from '../../utils/currency';

export const DENOMINATIONS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000];

/** Lo que suman los billetes y monedas contados (pesos). */
export const denominationTotal = (data) =>
  data ? DENOMINATIONS.reduce((sum, value) => sum + Number(data[`dec${value}`] ?? 0) * value, 0) : null;

const amount = (value) => Number(value ?? 0);
const differs = (a, b) => amount(a).toFixed(2) !== amount(b).toFixed(2);

const OTHER_METHODS = [
  { key: 'mercadoPago', label: 'MercadoPago' },
  { key: 'pedidosYa', label: 'PedidosYa' },
  { key: 'pos', label: 'POS' },
];

/** Egresos de efectivo de una moneda: compras (1), depósitos (2) y retiros (3). Un depósito bancario también retira efectivo físico de la caja. */
const outflowsOf = (movements, code) => {
  const own = (movements ?? []).filter((movement) => (movement.currency || DEFAULT_CURRENCY) === code);
  const total = (type) => own.filter((movement) => type == null || movement.type === type).reduce((sum, movement) => sum + amount(movement.amount), 0);
  const purchases = total(1);
  const deposits = total(2);
  const withdrawals = total(3);
  return { purchases, deposits, withdrawals, all: total(null) };
};

/**
 * El resumen de la caja en una moneda, con la misma forma para todas: lo que había al abrir, lo vendido en efectivo, lo que salió,
 * lo que tendría que haber, lo que se declaró y la diferencia; y por cada otro medio lo que anotó el sistema contra lo declarado.
 * Los pesos salen de los campos de la caja y las demás monedas de sus saldos (`balances`).
 */
export function buildCurrencySummaries(openData, closeData) {
  if (!openData) return [];
  const summaries = [];

  // Pesos: se cuentan billetes y monedas.
  const openingCash = denominationTotal(openData);
  const declared = closeData ? denominationTotal(closeData) : null;
  const out = outflowsOf(openData.movements, DEFAULT_CURRENCY);
  const expected = openingCash + amount(openData.efectivo) - (out.purchases + out.deposits + out.withdrawals);
  summaries.push({
    code: DEFAULT_CURRENCY,
    isBase: true,
    openingCash,
    cashSales: amount(openData.efectivo),
    purchases: out.purchases,
    deposits: out.deposits,
    withdrawals: out.withdrawals,
    outflows: out.purchases + out.deposits + out.withdrawals,
    expected,
    declared,
    difference: declared == null ? null : declared - expected,
    mismatch: declared != null && differs(declared, expected),
    others: OTHER_METHODS.map(({ key, label }) => ({
      key, label, system: amount(openData[key]), declared: closeData ? amount(closeData[key]) : null,
      mismatch: closeData ? differs(openData[key], closeData[key]) : false,
    })),
  });

  // Otras monedas: un solo monto de efectivo contado, sin billetes.
  const codes = Array.from(new Set([...(openData.balances || []), ...(closeData?.balances || [])].map((balance) => balance.currency)));
  codes.forEach((code) => {
    const opened = (openData.balances || []).find((balance) => balance.currency === code);
    const closed = (closeData?.balances || []).find((balance) => balance.currency === code);
    const outflow = outflowsOf(openData.movements, code);
    const opening = amount(opened?.declaredCash);
    const sales = amount(opened?.efectivo);
    const expectedForeign = opening + sales - outflow.all;
    const declaredForeign = closed ? amount(closed.declaredCash) : null;
    const difference = closed ? (closed.discrepancy ?? declaredForeign - expectedForeign) : null;
    summaries.push({
      code,
      isBase: false,
      openingCash: opening,
      cashSales: sales,
      purchases: outflow.purchases,
      deposits: outflow.deposits,
      withdrawals: outflow.withdrawals,
      outflows: outflow.all,
      expected: expectedForeign,
      declared: declaredForeign,
      difference,
      mismatch: difference != null && Math.abs(difference) > 0.005,
      others: OTHER_METHODS.map(({ key, label }) => ({
        key, label, system: amount(opened?.[key]), declared: closed ? amount(closed[key]) : null,
        mismatch: closed ? differs(opened?.[key], closed[key]) : false,
      })),
    });
  });
  return summaries;
}
